import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  Eye,
  FileText,
  Folder,
  FolderOpen,
  GitMerge,
  Info,
  SquarePen,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';

import {
  CATALOG_VERSION_WRONG_STATE,
  CatalogApiError,
  PROFILE_ALREADY_ARCHIVED,
  PROFILE_ARCHIVED_NOT_EDITABLE,
  PROFILE_MERGE_CONFIRMATION_MISMATCH,
  PROFILE_MERGE_OPERAND_ARCHIVED,
  PROFILE_MERGE_SAME_PROFILE,
  PROFILE_NOT_ARCHIVED,
  type CatalogVersionStateDto,
  type CompetencesByLevelDto,
  type CorrelationEdgeView,
  type ProfileView,
  type SeniorityLevelDto,
} from '@/api/catalog-client';
import {
  useArchiveProfile,
  useCatalogEdges,
  useCatalogProfiles,
  useCloneProfile,
  useMergeProfiles,
  useProfileCampaignUsage,
  useRestoreProfile,
  useSuggestProfileCode,
  useUpsertProfile,
} from '@/api/use-catalog-edition';
import { RichHtmlEditor } from '@/components/rich-html-editor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Sheet } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { SENIORITY_LEVELS } from '@/lib/catalog-labels';
import {
  correlationPercentToWeight,
  correlationWeightToPercent,
} from './catalog-edition-helpers';

import {
  groupProfilesByFamily,
  type ProfileFamilyNode,
} from './catalog-profile-tree';
import { CorrelationMatrixSection } from './catalog-correlation';

// =============================================================================
// catalog-edition-screen.tsx — A-09 édition profil + MATRICE de corrélation (§2.4).
// =============================================================================
// BC catalog, MONDE ADMIN d'ÉCRITURE (P-05 : routes admin distinctes des lectures S8
// A-08/F-05). Édite le contenu d'une version DRAFT : upsert d'un Profile (A-09) et
// upsert d'une arête de corrélation. catalog = référentiel GLOBAL : AUCUNE donnée
// nominative, AUCUNE PII.
//
// DBU §2.4 (l.961-973) : la matrice de corrélation est représentée en PROFILS EN LIGNES
// × COLONNES (l.961) ; chaque cellule porte le taux 0-100 % (l.963) éditable directement
// avec une ÉTAPE DE CONFIRMATION (l.965) ; carte de chaleur optionnelle (l.967, couleur
// TOUJOURS doublée du % textuel) ; filtre « corrélations actives » (l.969) ; badge du
// nombre de liaisons par profil (l.971) ; export CSV (l.973) ; profils corrélés cliquables
// (l.947). L'état de vue (famille / actives / heatmap / focus) est porté par l'URL
// (search params ; RULE_REF_006 : jamais localStorage).
//
// R-2 (DBU §2.4 l.882-972) : la corrélation est SAISIE et AFFICHÉE de 0 à 100 % côté IHM.
// Le contrat (Published Language / DTO / API / Prisma) reste 0-1 (Decimal(4,3)) : conversion
// d'AFFICHAGE PURE côté front — afficher weight×100 + « % », diviser la saisie ÷100 avant
// envoi, préremplir l'édition ×100. Le fil réseau/DB ne change pas. La plage [0,1] reste
// l'autorité serveur (garde front 0-100 non-autoritaire).
//
// L'AUTORITÉ (RBAC AdminSessionGuard, inv.1 unicité perpétuelle, inv.2 édition réservée
// DRAFT, poids ∈ [0,1]) est l'API ; l'UI relaie l'intention et affiche l'erreur serveur.
//
// WCAG 2.1 AA : <form> avec <label> liés ; <table> matrice avec <caption>/<th scope="col">
// /<th scope="row"> ; cellule éditable = <button>/<input> avec label contextuel (profil
// ligne × profil colonne) ; heatmap doublée du % textuel ; filtres <label> liés ;
// messages role="status"/role="alert" ; navigation clavier native.

// Helpers PURS partagés déplacés dans catalog-edition-helpers.ts (découpage du monolithe).
// Ré-exportés ici pour préserver les imports existants (tests R-2 des conversions notamment).
export { correlationWeightToPercent, correlationPercentToWeight };

/**
 * État de vue de la matrice, porté par l'URL (search params TanStack ; RULE_REF_006 :
 * jamais localStorage). `famille`/`actives` réduisent la grille (mitigation perf 82×82) ;
 * `heatmap` est purement présentationnel (couleur doublée du % textuel) ; `focus` surligne
 * la ligne/colonne d'un profil (navigation depuis les chips « profils corrélés »).
 */
export interface CatalogMatrixView {
  /** Restreint lignes+colonnes à une famille de profil (l.908). */
  readonly famille?: string;
  /** N'affiche que les profils ayant ≥ 1 corrélation active (l.969). */
  readonly actives?: boolean;
  /** Active la carte de chaleur (l.967) — le % textuel reste TOUJOURS présent. */
  readonly heatmap?: boolean;
  /** Code du profil surligné dans la grille (chips « profils corrélés », l.947). */
  readonly focus?: string;
}

export interface CatalogEditionScreenProps {
  /** Version DRAFT éditée (ressource d'URL, portée par l'adaptateur routeur). */
  readonly catalogVersionId: string;
  /**
   * État de la version éditée (résolu par l'adaptateur routeur, A-08). Gouverne l'action
   * d'ARCHIVAGE de profil (A-CATALOG §2.4) : le bouton « Archiver » n'est proposé QUE sur une
   * version DRAFT (autorité finale = API : 409 CATALOG_VERSION_WRONG_STATE sinon). `undefined`
   * (état non encore chargé / rendu hors routeur) ⇒ action masquée par prudence.
   */
  readonly versionState?: CatalogVersionStateDto | undefined;
  /**
   * État de vue de la matrice (contrôlé — porté par l'URL via l'adaptateur routeur).
   * Absent ⇒ l'écran retombe sur un état in-memory local (rendu hors routeur / test).
   */
  readonly matrixView?: CatalogMatrixView;
  /** Notifie un changement d'état de vue (met à jour l'URL côté adaptateur). */
  readonly onMatrixViewChange?: (next: CatalogMatrixView) => void;
}

export function CatalogEditionScreen({
  catalogVersionId,
  versionState,
  matrixView,
  onMatrixViewChange,
}: CatalogEditionScreenProps): React.JSX.Element {
  // Contrôlé si l'adaptateur fournit l'état d'URL ; sinon état in-memory (RULE_REF_006).
  const [internalView, setInternalView] = React.useState<CatalogMatrixView>({});
  const view = matrixView ?? internalView;
  const setView = onMatrixViewChange ?? setInternalView;

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Édition de la version de catalogue
        </h1>
        <p className="text-muted-foreground">
          Référentiel global. Édition réservée aux versions en brouillon. Version&nbsp;
          <span className="font-mono text-foreground">{catalogVersionId}</span>.
        </p>
      </header>

      <ProfileEditionSection
        catalogVersionId={catalogVersionId}
        versionState={versionState}
      />
      <CorrelationMatrixSection
        catalogVersionId={catalogVersionId}
        view={view}
        onViewChange={setView}
      />
    </div>
  );
}

// =============================================================================
// A-09 — édition d'un Profile (upsert) + A-CATALOG §2.4 archivage.
// =============================================================================

/** État d'un Profile → libellé FR + variant de badge (WCAG : le LIBELLÉ porte l'information). */
function profileStateLabel(state: ProfileView['state']): {
  readonly label: string;
  readonly variant: 'default' | 'secondary';
} {
  return state === 'Archived'
    ? { label: 'Archivé', variant: 'secondary' }
    : { label: 'Actif', variant: 'default' };
}

/**
 * Traduit l'échec d'archivage (A-CATALOG §2.4) en libellé métier À PARTIR DU CODE machine STABLE
 * (jamais du message serveur, non stable). Les 409 STRUCTURÉS de l'API :
 *  • CATALOG_VERSION_WRONG_STATE : la version n'est pas un brouillon.
 *  • PROFILE_ALREADY_ARCHIVED : profil déjà archivé (idempotence).
 * Repli sur le message serveur pour tout autre code (404, 500…).
 */
function archiveErrorMessage(error: Error): string {
  if (error instanceof CatalogApiError) {
    if (error.code === CATALOG_VERSION_WRONG_STATE) {
      return 'Archivage possible uniquement sur un brouillon.';
    }
    if (error.code === PROFILE_ALREADY_ARCHIVED) {
      return 'Profil déjà archivé.';
    }
  }
  return error.message;
}

/**
 * Traduit l'échec de FUSION (R15-CAT-01, DBU §2.4 l.866) en libellé métier depuis le CODE machine STABLE :
 *  • CATALOG_VERSION_WRONG_STATE : la version n'est pas un brouillon.
 *  • PROFILE_MERGE_SAME_PROFILE : source et cible identiques.
 *  • PROFILE_MERGE_CONFIRMATION_MISMATCH : le code de confirmation ne correspond pas au profil source.
 *  • PROFILE_MERGE_OPERAND_ARCHIVED : l'un des deux profils est archivé.
 * Repli sur le message serveur pour tout autre code (404, 500…).
 */
function mergeErrorMessage(error: Error): string {
  if (error instanceof CatalogApiError) {
    if (error.code === CATALOG_VERSION_WRONG_STATE) {
      return 'Fusion possible uniquement sur un brouillon.';
    }
    if (error.code === PROFILE_MERGE_SAME_PROFILE) {
      return 'La source et la cible doivent être deux profils distincts.';
    }
    if (error.code === PROFILE_MERGE_CONFIRMATION_MISMATCH) {
      return 'Le code de confirmation ne correspond pas au profil source.';
    }
    if (error.code === PROFILE_MERGE_OPERAND_ARCHIVED) {
      return 'Les deux profils doivent être actifs pour être fusionnés.';
    }
  }
  return error.message;
}

/**
 * Traduit l'échec d'ÉDITION du descriptif (upsert A-09) en libellé métier depuis le CODE machine STABLE :
 *  • CATALOG_VERSION_WRONG_STATE : la version n'est pas un brouillon.
 *  • PROFILE_ARCHIVED_NOT_EDITABLE (DBU §2.4 l.957) : le profil est archivé — restaurer avant d'éditer.
 * Repli sur le message serveur (400 de validation HTML, etc.) pour tout autre code.
 */
function editErrorMessage(error: Error): string {
  if (error instanceof CatalogApiError) {
    if (error.code === CATALOG_VERSION_WRONG_STATE) {
      return 'Modification possible uniquement sur un brouillon.';
    }
    if (error.code === PROFILE_ARCHIVED_NOT_EDITABLE) {
      return 'Ce profil est archivé : restaurez-le avant de le modifier.';
    }
  }
  return error.message;
}

function ProfileEditionSection({
  catalogVersionId,
  versionState,
}: {
  readonly catalogVersionId: string;
  readonly versionState: CatalogVersionStateDto | undefined;
}): React.JSX.Element {
  const list = useCatalogProfiles(catalogVersionId);
  const edgesQuery = useCatalogEdges(catalogVersionId);
  const archive = useArchiveProfile();
  const clone = useCloneProfile();
  const restore = useRestoreProfile();
  const merge = useMergeProfiles();
  /** Profil en cours d'édition (null = création). État in-memory (RULE_REF_006). */
  const [editing, setEditing] = React.useState<ProfileView | null>(null);
  /** Discriminant pour réinitialiser le formulaire entre création/édition. */
  const [formKey, setFormKey] = React.useState(0);
  /** Profil ciblé par la confirmation d'archivage (null = modale fermée). In-memory. */
  const [archiveTarget, setArchiveTarget] = React.useState<ProfileView | null>(null);
  /** Profil affiché en fiche read-only (null = drawer fermé). In-memory. */
  const [viewing, setViewing] = React.useState<ProfileView | null>(null);
  /** Profil SOURCE ciblé par la fusion (null = modale fermée). In-memory (R15-CAT-01). */
  const [mergeSource, setMergeSource] = React.useState<ProfileView | null>(null);
  /** Code du profil CIBLE choisi dans la modale de fusion (« » = non choisi). In-memory. */
  const [mergeTargetCode, setMergeTargetCode] = React.useState<string>('');
  /** Confirmation textuelle du code source dans la modale de fusion. In-memory. */
  const [mergeConfirm, setMergeConfirm] = React.useState<string>('');
  /** C1 — recherche/filtre des profils (libellé/famille/sous-famille/code + niveau d'expertise). */
  const [profileQuery, setProfileQuery] = React.useState('');
  // Filtre de niveau d'expertise à sélection MULTIPLE (OR) : un profil matche s'il porte AU MOINS UN
  // des niveaux cochés. Vide = tous les niveaux (aucun filtre).
  const [levelFilters, setLevelFilters] = React.useState<readonly string[]>([]);

  // C1 — profils affichés dans l'arbre après recherche plein-texte + filtre de niveaux d'expertise.
  // N'affecte QUE l'arborescence (les autres lectures, ex. cible de fusion, restent sur list.data).
  const filteredProfiles = React.useMemo(() => {
    const data = list.data ?? [];
    const q = profileQuery.trim().toLowerCase();
    return data.filter(
      (p) =>
        (levelFilters.length === 0 ||
          p.availableLevels.some((l) => levelFilters.includes(l))) &&
        (q === '' ||
          p.libelle.toLowerCase().includes(q) ||
          p.famille.toLowerCase().includes(q) ||
          p.sousFamille.toLowerCase().includes(q) ||
          p.profileCode.toLowerCase().includes(q)),
    );
  }, [list.data, profileQuery, levelFilters]);

  // L'archivage/restauration/clonage n'est proposé QUE sur une version DRAFT (autorité finale = API).
  const canArchive = versionState === 'DRAFT';

  // §2.4 (A0R-10) — usage du profil ciblé par les campagnes EN COURS (avertissement INFORMATIONNEL,
  // parité S23 mais SANS blocage : la version de catalogue d'une campagne est figée, impact non
  // rétroactif). Actif uniquement quand une cible d'archivage existe (hook piloté par archiveTarget).
  const campaignUsage = useProfileCampaignUsage(
    catalogVersionId,
    archiveTarget?.profileCode ?? null,
  );
  const campaignUsageCount = campaignUsage.data?.length ?? 0;

  // Corrélations ACTIVES par profil (l.958/977 : l'avertissement d'archivage les LISTE). Masque
  // les arêtes Archived (parité matrice active). Index from|to → libellé de l'autre extrémité.
  const activeEdges = React.useMemo<readonly CorrelationEdgeView[]>(
    () => (edgesQuery.data ?? []).filter((e) => e.state === 'Active'),
    [edgesQuery.data],
  );
  const labelByCode = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of list.data ?? []) {
      m.set(p.profileCode, p.libelle);
    }
    return m;
  }, [list.data]);
  const correlationsOf = React.useCallback(
    (profileCode: string): readonly string[] =>
      activeEdges
        .filter((e) => e.profileFrom === profileCode || e.profileTo === profileCode)
        .map((e) => {
          const other = e.profileFrom === profileCode ? e.profileTo : e.profileFrom;
          return labelByCode.get(other) ?? other;
        }),
    [activeEdges, labelByCode],
  );

  const requestClone = React.useCallback(
    (profile: ProfileView): void => {
      clone.reset();
      clone.mutate({ catalogVersionId, profileCode: profile.profileCode });
    },
    [clone, catalogVersionId],
  );

  const requestRestore = React.useCallback(
    (profile: ProfileView): void => {
      restore.reset();
      restore.mutate({ catalogVersionId, profileCode: profile.profileCode });
    },
    [restore, catalogVersionId],
  );

  /** C2 — le formulaire vit dans un VOLET LATÉRAL (Sheet), ouvert à la création ou à l'édition. */
  const [panelOpen, setPanelOpen] = React.useState(false);
  const startCreate = React.useCallback((): void => {
    setEditing(null);
    setFormKey((k) => k + 1);
    setPanelOpen(true);
  }, []);
  const startEdit = React.useCallback((profile: ProfileView): void => {
    setEditing(profile);
    setFormKey((k) => k + 1);
    setPanelOpen(true);
  }, []);
  const closePanel = React.useCallback((): void => {
    setPanelOpen(false);
  }, []);

  const requestArchive = React.useCallback(
    (profile: ProfileView): void => {
      archive.reset();
      setArchiveTarget(profile);
    },
    [archive],
  );

  const cancelArchive = React.useCallback((): void => {
    setArchiveTarget(null);
  }, []);

  // R15-CAT-01 — FUSION : cibles éligibles = profils ACTIFS de la version, SAUF la source (deux profils
  // distincts requis ; l'autorité finale reste l'API). Triées par libellé pour un rendu déterministe.
  const mergeTargetOptions = React.useMemo<readonly ProfileView[]>(
    () =>
      (list.data ?? [])
        .filter(
          (p) => p.state === 'Active' && p.profileCode !== mergeSource?.profileCode,
        )
        .slice()
        .sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr')),
    [list.data, mergeSource],
  );
  // Confirmation textuelle = code source EXACT (garde-fou anti-fausse-manœuvre, parité promotion) ET une
  // cible distincte sélectionnée : sans les DEUX, le bouton « Fusionner » reste désactivé.
  const mergeConfirmMatches =
    mergeSource !== null && mergeConfirm.trim() === mergeSource.profileCode;
  const mergeTargetValid =
    mergeSource !== null &&
    mergeTargetCode !== '' &&
    mergeTargetCode !== mergeSource.profileCode;

  const requestMerge = React.useCallback(
    (profile: ProfileView): void => {
      merge.reset();
      setMergeSource(profile);
      setMergeTargetCode('');
      setMergeConfirm('');
    },
    [merge],
  );

  const cancelMerge = React.useCallback((): void => {
    setMergeSource(null);
    setMergeTargetCode('');
    setMergeConfirm('');
  }, []);

  const confirmMerge = React.useCallback((): void => {
    if (mergeSource === null || !mergeConfirmMatches || !mergeTargetValid) {
      return;
    }
    merge.mutate(
      {
        catalogVersionId,
        sourceProfileCode: mergeSource.profileCode,
        targetProfileCode: mergeTargetCode,
        confirmationCode: mergeConfirm.trim(),
      },
      {
        onSuccess: () => {
          cancelMerge();
        },
      },
    );
  }, [
    merge,
    mergeSource,
    mergeConfirmMatches,
    mergeTargetValid,
    catalogVersionId,
    mergeTargetCode,
    mergeConfirm,
    cancelMerge,
  ]);

  const confirmArchive = React.useCallback((): void => {
    if (archiveTarget === null) {
      return;
    }
    archive.mutate(
      { catalogVersionId, profileCode: archiveTarget.profileCode },
      {
        onSuccess: () => {
          setArchiveTarget(null);
        },
        onError: (err) => {
          // Idempotence (PROFILE_ALREADY_ARCHIVED) : l'état serveur est DÉJÀ archivé — on
          // rafraîchit la preview pour refléter Profile.state=Archived (la modale montre le message).
          if (
            err instanceof CatalogApiError &&
            err.code === PROFILE_ALREADY_ARCHIVED
          ) {
            void list.refetch();
          }
        },
      },
    );
  }, [archive, archiveTarget, catalogVersionId, list]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profils (A-09)</CardTitle>
        <CardDescription>
          Créez ou modifiez un profil de la version brouillon. Le code de profil est
          alloué et garanti unique par le serveur.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-end">
          <Button type="button" onClick={startCreate}>
            Nouveau profil
          </Button>
        </div>

        <Sheet
          open={panelOpen}
          onClose={closePanel}
          label={
            editing !== null
              ? `Modifier le profil ${editing.profileCode}`
              : 'Créer un profil'
          }
        >
          <ProfileForm
            key={`profile-form-${String(formKey)}`}
            catalogVersionId={catalogVersionId}
            editing={editing}
            onCancelEdit={closePanel}
            onSaved={closePanel}
          />
        </Sheet>

        <section aria-labelledby="profiles-list-heading" className="space-y-3">
          <h3
            id="profiles-list-heading"
            className="text-sm font-medium text-foreground"
          >
            Profils existants
          </h3>
          <p className="text-xs text-muted-foreground">
            Catalogue organisé en arborescence par famille et sous-famille. Chaque
            nœud est repliable. Recherchez et filtrez pour cibler un profil.
          </p>

          <div
            className="flex flex-wrap items-end gap-3"
            role="search"
            aria-label="Rechercher un profil dans le catalogue"
          >
            <div className="min-w-[220px] flex-1 space-y-1">
              <Label htmlFor="profile-search">Rechercher un profil</Label>
              <Input
                id="profile-search"
                type="search"
                value={profileQuery}
                onChange={(e) => setProfileQuery(e.target.value)}
                placeholder="Libellé, famille, sous-famille ou code…"
              />
            </div>
            <div className="space-y-1">
              <Label id="profile-level-label">Niveau d'expertise</Label>
              <LevelMultiSelect
                labelledBy="profile-level-label"
                levels={SENIORITY_LEVELS}
                selected={levelFilters}
                onChange={setLevelFilters}
              />
            </div>
          </div>

          {list.isPending ? (
            <p role="status" className="text-sm text-muted-foreground">
              Chargement des profils…
            </p>
          ) : list.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Erreur de chargement : {list.error.message}
            </p>
          ) : list.data.length === 0 ? (
            <p role="status" className="text-sm text-muted-foreground">
              Aucun profil dans cette version pour le moment.
            </p>
          ) : filteredProfiles.length === 0 ? (
            <p role="status" className="text-sm text-muted-foreground">
              Aucun profil ne correspond à la recherche.
            </p>
          ) : (
            <ProfileFamilyTree
              profiles={filteredProfiles}
              canArchive={canArchive}
              cloneIsPending={clone.isPending}
              restoreIsPending={restore.isPending}
              onView={setViewing}
              onEdit={startEdit}
              onClone={requestClone}
              onArchive={requestArchive}
              onRestore={requestRestore}
              onMerge={requestMerge}
            />
          )}
        </section>

        <ConfirmDialog
          open={archiveTarget !== null}
          title="Archiver ce profil ?"
          description={
            archiveTarget !== null
              ? `Le profil « ${archiveTarget.libelle} » (${archiveTarget.profileCode}) passera à l'état Archivé et ses corrélations associées seront archivées. Cette action reste bornée au brouillon.`
              : ''
          }
          confirmLabel={archive.isPending ? 'Archivage…' : 'Archiver'}
          confirmDisabled={archive.isPending}
          onConfirm={confirmArchive}
          onCancel={cancelArchive}
        >
          {archiveTarget !== null &&
          correlationsOf(archiveTarget.profileCode).length > 0 ? (
            <div className="rounded-md border border-border bg-muted/30 p-3">
              <p className="text-sm font-medium text-foreground">
                Corrélations actives qui seront archivées (
                {correlationsOf(archiveTarget.profileCode).length}) :
              </p>
              <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
                {correlationsOf(archiveTarget.profileCode).map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {/* §2.4 (A0R-10) — impact « campagnes en cours » : bloc INFORMATIONNEL, distinct du bloc
              corrélations. N'affecte JAMAIS le bouton (confirmDisabled inchangé) : une campagne fige sa
              version de catalogue, l'archivage n'a aucun effet rétroactif. */}
          {archiveTarget !== null && campaignUsageCount > 0 ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
              <p className="text-sm font-medium text-foreground">
                Ce profil est référencé par {campaignUsageCount} campagne(s) en
                cours. L&apos;archivage n&apos;altère PAS ces campagnes : leur version
                de catalogue est figée (avertissement informationnel).
              </p>
              <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
                {(campaignUsage.data ?? []).map((c) => (
                  <li key={c.campaignId}>
                    {c.intitule} ({c.state})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div aria-live="polite" aria-atomic="true">
            {archive.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {archiveErrorMessage(archive.error)}
              </p>
            ) : null}
          </div>
        </ConfirmDialog>

        {/* R15-CAT-01 — FUSION de profils (DBU §2.4 l.866). Modale DS (ConfirmDialog) : sélection de la
            cible, aperçu des corrélations transférées, CONFIRMATION TEXTUELLE du code source (garde-fou,
            parité promotion). Le bouton reste désactivé tant que la cible n'est pas choisie ET que le code
            source n'est pas resaisi à l'identique. L'autorité (transfert + archivage) est l'API. */}
        <ConfirmDialog
          open={mergeSource !== null}
          title="Fusionner ce profil ?"
          description={
            mergeSource !== null
              ? `Le profil « ${mergeSource.libelle} » (${mergeSource.profileCode}) sera fusionné dans un autre profil : ses corrélations seront transférées vers la cible, puis ce profil sera archivé. Cette action est sensible : confirmez en resaisissant le code du profil source.`
              : ''
          }
          confirmLabel={merge.isPending ? 'Fusion…' : 'Fusionner'}
          confirmDisabled={
            merge.isPending || !mergeConfirmMatches || !mergeTargetValid
          }
          onConfirm={confirmMerge}
          onCancel={cancelMerge}
        >
          {mergeSource !== null ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="merge-target-select">
                  Profil cible (destination des corrélations)
                </Label>
                <select
                  id="merge-target-select"
                  value={mergeTargetCode}
                  onChange={(e) => setMergeTargetCode(e.target.value)}
                  aria-describedby="merge-target-hint"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Sélectionnez un profil cible…</option>
                  {mergeTargetOptions.map((p) => (
                    <option key={p.profileCode} value={p.profileCode}>
                      {p.libelle} ({p.profileCode})
                    </option>
                  ))}
                </select>
                <p id="merge-target-hint" className="text-xs text-muted-foreground">
                  La cible RECUEILLE les corrélations du profil source, qui est ensuite
                  archivé.
                </p>
              </div>

              {correlationsOf(mergeSource.profileCode).length > 0 ? (
                <div className="rounded-md border border-border bg-muted/30 p-3">
                  <p className="text-sm font-medium text-foreground">
                    Corrélations actives transférées vers la cible (
                    {correlationsOf(mergeSource.profileCode).length}) :
                  </p>
                  <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
                    {correlationsOf(mergeSource.profileCode).map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Ce profil n&apos;a aucune corrélation active à transférer.
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="merge-confirm-input">
                  Saisissez « {mergeSource.profileCode} » pour confirmer
                </Label>
                <Input
                  id="merge-confirm-input"
                  value={mergeConfirm}
                  onChange={(e) => setMergeConfirm(e.target.value)}
                  autoComplete="off"
                  aria-describedby="merge-confirm-hint"
                />
                <p id="merge-confirm-hint" className="text-xs text-muted-foreground">
                  La fusion est confirmée uniquement si le code saisi correspond
                  exactement au profil source.
                </p>
              </div>

              <div aria-live="polite" aria-atomic="true">
                {merge.isError ? (
                  <p role="alert" className="text-sm text-destructive">
                    {mergeErrorMessage(merge.error)}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </ConfirmDialog>

        <div aria-live="polite" aria-atomic="true">
          {clone.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Échec du clonage : {clone.error.message}
            </p>
          ) : null}
          {clone.isSuccess ? (
            <p role="status" className="text-sm text-success">
              Profil cloné : {clone.data.profileCode}.
            </p>
          ) : null}
          {restore.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {restoreErrorMessage(restore.error)}
            </p>
          ) : null}
          {restore.isSuccess ? (
            <p role="status" className="text-sm text-success">
              Profil restauré.
            </p>
          ) : null}
          {merge.isSuccess ? (
            <p role="status" className="text-sm text-success">
              Profils fusionnés : corrélations transférées vers {merge.data.libelle}.
            </p>
          ) : null}
        </div>

        <ProfileViewDialog profile={viewing} onClose={() => setViewing(null)} />
      </CardContent>
    </Card>
  );
}

/**
 * Actions disponibles sur un profil (DBU §2.4 l.955 : visualiser, éditer, cloner, archiver ;
 * l.960 : restaurer). Regroupées pour éviter le forage de props à travers l'arborescence.
 */
interface ProfileRowActions {
  readonly canArchive: boolean;
  readonly cloneIsPending: boolean;
  readonly restoreIsPending: boolean;
  readonly onView: (profile: ProfileView) => void;
  readonly onEdit: (profile: ProfileView) => void;
  readonly onClone: (profile: ProfileView) => void;
  readonly onArchive: (profile: ProfileView) => void;
  readonly onRestore: (profile: ProfileView) => void;
  /** Ouvre la modale de FUSION avec ce profil comme SOURCE (R15-CAT-01). */
  readonly onMerge: (profile: ProfileView) => void;
}

/**
 * Filtre de niveau d'expertise à SÉLECTION MULTIPLE (liste déroulante à cases à cocher, sémantique OR).
 * Trigger = bouton nommé par `labelledBy` ; le panneau porte une case par niveau + une remise à zéro
 * « Tous les niveaux ». Ferme au clic-hors / Échap. Vide = aucun filtre (tous les niveaux affichés).
 */
function LevelMultiSelect({
  labelledBy,
  levels,
  selected,
  onChange,
}: {
  readonly labelledBy: string;
  readonly levels: readonly string[];
  readonly selected: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (level: string): void => {
    onChange(
      selected.includes(level)
        ? selected.filter((l) => l !== level)
        : [...selected, level],
    );
  };

  const summary =
    selected.length === 0
      ? 'Tous les niveaux'
      : selected.length === 1
        ? (selected[0] ?? '')
        : `${String(selected.length)} niveaux`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-labelledby={labelledBy}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="flex h-10 min-w-[190px] items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span
          className={
            selected.length === 0 ? 'text-muted-foreground' : 'text-foreground'
          }
        >
          {summary}
        </span>
        <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      </button>
      {open ? (
        <div
          role="group"
          aria-labelledby={labelledBy}
          className="absolute left-0 z-30 mt-1 min-w-[220px] rounded-md border border-border bg-card p-1 shadow-md"
        >
          <button
            type="button"
            onClick={() => {
              onChange([]);
            }}
            className="block w-full rounded-sm px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none"
          >
            Tous les niveaux
          </button>
          {levels.map((level) => (
            <label
              key={level}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <input
                type="checkbox"
                className="size-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                checked={selected.includes(level)}
                onChange={() => {
                  toggle(level);
                }}
              />
              {level}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Indentation (Tailwind littéral, scanné par JIT) par profondeur : famille=0, sous-famille=1, feuille=2. */
const INDENT_CLASS = ['pl-3', 'pl-8', 'pl-14'] as const;

/**
 * Ligne « dossier » (famille / sous-famille) façon Finder macOS : chevron + icône de dossier +
 * libellé + compteur, indentée selon la profondeur, occupant toute la largeur (colSpan). Repliable
 * au clic ET au clavier (bouton natif, `aria-expanded`) — l'état de pliage est porté par le parent.
 */
function FolderRow({
  depth,
  label,
  count,
  open,
  onToggle,
}: {
  readonly depth: 0 | 1;
  readonly label: string;
  readonly count: number;
  readonly open: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  const FolderIcon = open ? FolderOpen : Folder;
  return (
    <tr className="border-b border-border last:border-b-0">
      <td colSpan={4} className="p-0">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className={`flex w-full items-center gap-2 py-2 pr-3 text-left text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${INDENT_CLASS[depth]} ${depth === 0 ? 'font-semibold' : 'font-medium'}`}
        >
          <ChevronRight
            aria-hidden="true"
            className={`size-4 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
          />
          <FolderIcon
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground"
          />
          <span className="text-sm">{label}</span>
          <Badge variant={depth === 0 ? 'secondary' : 'outline'} className="ml-0.5">
            <span aria-hidden="true">{count}</span>
            <span className="sr-only">
              {count > 1 ? `${String(count)} profils` : '1 profil'}
            </span>
          </Badge>
        </button>
      </td>
    </tr>
  );
}

/**
 * Arborescence des profils façon Finder macOS : familles → sous-familles → profils (DBU §2.4 l.938),
 * rendue en UNE seule table (colonnes alignées globalement : Nom / Niveaux / État / Actions). Les
 * dossiers (famille, sous-famille) sont des lignes repliables (`FolderRow`, `aria-expanded`) ; les
 * profils sont des lignes-feuilles COMPACTES (`ProfileRow`). Groupement PUR côté front
 * (`groupProfilesByFamily`). L'état de pliage est LOCAL (un Set de clés repliées, défaut = tout
 * déplié) — RULE_REF_006 : jamais localStorage. La description (contextes / environnements) n'occupe
 * plus de colonne : elle apparaît au survol (infobulle `title`) sur la ligne-feuille.
 */
function ProfileFamilyTree({
  profiles,
  ...actions
}: { readonly profiles: readonly ProfileView[] } & ProfileRowActions): React.JSX.Element {
  const families = React.useMemo<readonly ProfileFamilyNode[]>(
    () => groupProfilesByFamily(profiles),
    [profiles],
  );
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const toggle = React.useCallback((key: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Toutes les clés de nœuds-dossiers (familles + sous-familles) : base de « tout replier ».
  const allFolderKeys = React.useMemo<readonly string[]>(() => {
    const keys: string[] = [];
    for (const family of families) {
      keys.push(`f:${family.famille}`);
      for (const sub of family.sousFamilles) {
        keys.push(`s:${family.famille}/${sub.sousFamille}`);
      }
    }
    return keys;
  }, [families]);
  const expandAll = React.useCallback((): void => {
    setCollapsed(new Set<string>());
  }, []);
  const collapseAll = React.useCallback((): void => {
    setCollapsed(new Set(allFolderKeys));
  }, [allFolderKeys]);

  // Menu contextuel (clic droit) « Tout déplier / Tout replier » — positionné au curseur (Finder).
  const [menu, setMenu] = React.useState<{ x: number; y: number } | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const onContextMenu = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.preventDefault();
      setMenu({ x: event.clientX, y: event.clientY });
    },
    [],
  );
  React.useEffect(() => {
    if (menu === null) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (
        menuRef.current !== null &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenu(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMenu(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    // Focalise le 1er item à l'ouverture (clavier / lecteur d'écran).
    window.requestAnimationFrame(() => {
      menuRef.current
        ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
        ?.focus();
    });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menu]);

  return (
    <div
      className="relative overflow-x-auto rounded-lg border border-border"
      onContextMenu={onContextMenu}
    >
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">
          Arborescence des profils par famille et sous-famille : nom, niveaux disponibles, état,
          actions. La description de chaque profil apparaît au survol.
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="px-3 py-2 font-medium">
              Nom
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Niveaux
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              État
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {families.map((family) => {
            const famKey = `f:${family.famille}`;
            const famOpen = !collapsed.has(famKey);
            return (
              <React.Fragment key={family.famille}>
                <FolderRow
                  depth={0}
                  label={family.famille}
                  count={family.profileCount}
                  open={famOpen}
                  onToggle={() => {
                    toggle(famKey);
                  }}
                />
                {famOpen
                  ? family.sousFamilles.map((sub) => {
                      const subKey = `s:${family.famille}/${sub.sousFamille}`;
                      const subOpen = !collapsed.has(subKey);
                      return (
                        <React.Fragment key={sub.sousFamille}>
                          <FolderRow
                            depth={1}
                            label={sub.sousFamille}
                            count={sub.profiles.length}
                            open={subOpen}
                            onToggle={() => {
                              toggle(subKey);
                            }}
                          />
                          {subOpen
                            ? sub.profiles.map((profile) => (
                                <ProfileRow
                                  key={profile.profileCode}
                                  profile={profile}
                                  {...actions}
                                />
                              ))
                            : null}
                        </React.Fragment>
                      );
                    })
                  : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {menu !== null ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Actions de l'arborescence"
          tabIndex={-1}
          className="fixed z-30 min-w-[12rem] rounded-md border border-border bg-card p-1 text-card-foreground shadow-md"
          style={{ left: menu.x, top: menu.y }}
        >
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              expandAll();
              setMenu(null);
            }}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none"
          >
            <ChevronsUpDown aria-hidden="true" className="size-4" />
            Tout déplier
          </button>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              collapseAll();
              setMenu(null);
            }}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none"
          >
            <ChevronsDownUp aria-hidden="true" className="size-4" />
            Tout replier
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Bouton d'action en ICÔNE (ghost, compact) : nom accessible OBLIGATOIRE (aria-label) + infobulle. */
function IconAction({
  icon: Icon,
  label,
  title,
  onClick,
  disabled = false,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly title?: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
}): React.JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={title ?? label}
    >
      <Icon aria-hidden="true" className="size-4" />
    </Button>
  );
}

/** Pastilles compactes des niveaux (initiale D/M/E/R ; libellé complet au survol). Ordre canonique. */
function LevelPills({
  levels,
}: {
  readonly levels: readonly string[];
}): React.JSX.Element {
  const known = (SENIORITY_LEVELS as readonly string[]).filter((l) =>
    levels.includes(l),
  );
  const extras = levels.filter(
    (l) => !(SENIORITY_LEVELS as readonly string[]).includes(l),
  );
  const ordered = [...known, ...extras];
  if (ordered.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {ordered.map((lvl) => (
        <span
          key={lvl}
          title={lvl}
          className="inline-flex size-6 items-center justify-center rounded-full border border-border bg-muted/50 font-mono text-xs font-medium text-foreground"
        >
          <span aria-hidden="true">{lvl.trim().charAt(0).toUpperCase()}</span>
          <span className="sr-only">{lvl}</span>
        </span>
      ))}
    </span>
  );
}

/** Descriptif au survol (contextes / environnements, DBU §2.4 l.941) — n'occupe plus de colonne. */
function profileHoverDescription(profile: ProfileView): string {
  const contextes = profile.contextes?.trim();
  const environnements = profile.environnements?.trim();
  return [
    `Contextes : ${contextes !== undefined && contextes.length > 0 ? contextes : 'Non renseigné'}`,
    `Environnements : ${environnements !== undefined && environnements.length > 0 ? environnements : 'Non renseigné'}`,
  ].join('\n');
}

/**
 * Ligne « feuille » (profil) façon Finder, COMPACTE (DBU §2.4) : icône fichier + code + libellé, avec
 * la description en INFOBULLE au survol (icône Info) ; puis pastilles de niveaux (initiale), état, et
 * actions en ICÔNES. Les noms accessibles des actions sont conservés (aria-label) pour l'assistance.
 */
function ProfileRow({
  profile,
  canArchive,
  cloneIsPending,
  restoreIsPending,
  onView,
  onEdit,
  onClone,
  onArchive,
  onRestore,
  onMerge,
}: { readonly profile: ProfileView } & ProfileRowActions): React.JSX.Element {
  const { label, variant } = profileStateLabel(profile.state);
  const description = profileHoverDescription(profile);
  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-muted/30">
      <td className={`py-1.5 pr-3 align-middle ${INDENT_CLASS[2]}`}>
        <span className="flex items-center gap-2">
          <FileText
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground"
          />
          <span className="font-mono text-xs text-muted-foreground">
            {profile.profileCode}
          </span>
          <span className="text-foreground">{profile.libelle}</span>
          <span
            title={description}
            className="inline-flex shrink-0 cursor-help text-muted-foreground/70 hover:text-foreground"
          >
            <Info aria-hidden="true" className="size-3.5" />
            <span className="sr-only">{`Description du profil ${profile.libelle}`}</span>
          </span>
        </span>
      </td>
      <td className="px-3 py-1.5 align-middle">
        <LevelPills levels={profile.availableLevels} />
      </td>
      <td className="px-3 py-1.5 align-middle">
        <Badge variant={variant}>{label}</Badge>
      </td>
      <td className="px-3 py-1.5 align-middle">
        <span className="flex items-center justify-end gap-0.5">
          <IconAction
            icon={Eye}
            label={`Visualiser le profil ${profile.libelle}`}
            onClick={() => {
              onView(profile);
            }}
          />
          {profile.state === 'Active' ? (
            <IconAction
              icon={SquarePen}
              label={`Modifier le profil ${profile.libelle}`}
              onClick={() => {
                onEdit(profile);
              }}
            />
          ) : null}
          {canArchive ? (
            <IconAction
              icon={Copy}
              label={`Cloner le profil ${profile.libelle}`}
              disabled={cloneIsPending}
              onClick={() => {
                onClone(profile);
              }}
            />
          ) : null}
          {canArchive && profile.state === 'Active' ? (
            <IconAction
              icon={GitMerge}
              label={`Fusionner le profil ${profile.libelle}`}
              onClick={() => {
                onMerge(profile);
              }}
            />
          ) : null}
          {canArchive && profile.state === 'Active' ? (
            <IconAction
              icon={Archive}
              label="Archiver"
              title={`Archiver le profil ${profile.libelle}`}
              onClick={() => {
                onArchive(profile);
              }}
            />
          ) : null}
          {canArchive && profile.state === 'Archived' ? (
            <IconAction
              icon={ArchiveRestore}
              label={`Restaurer le profil ${profile.libelle}`}
              disabled={restoreIsPending}
              onClick={() => {
                onRestore(profile);
              }}
            />
          ) : null}
        </span>
      </td>
    </tr>
  );
}

/**
 * Traduit l'échec de RESTAURATION (DBU §2.4 l.960) en libellé métier depuis le CODE machine STABLE :
 *  • CATALOG_VERSION_WRONG_STATE : la version n'est pas un brouillon.
 *  • PROFILE_NOT_ARCHIVED : le profil n'est pas archivé (restauration inapplicable).
 * Repli sur le message serveur pour tout autre code.
 */
function restoreErrorMessage(error: Error): string {
  if (error instanceof CatalogApiError) {
    if (error.code === CATALOG_VERSION_WRONG_STATE) {
      return 'Restauration possible uniquement sur un brouillon.';
    }
    if (error.code === PROFILE_NOT_ARCHIVED) {
      return 'Ce profil n’est pas archivé.';
    }
  }
  return error.message;
}

/**
 * Fiche read-only d'un profil (VISUALISER, DBU §2.4 l.950). Modale accessible (role="dialog",
 * aria-modal) fermée par défaut ⇒ n'entre pas dans l'audit a11y au repos. Rend le descriptif de
 * missions dans un <iframe sandbox=""> (contenu INERTE, jamais dans le DOM parent) et les compétences
 * par niveau en listes. Aucune donnée nominative (référentiel GLOBAL).
 */
function ProfileViewDialog({
  profile,
  onClose,
}: {
  readonly profile: ProfileView | null;
  readonly onClose: () => void;
}): React.JSX.Element | null {
  const closeRef = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => {
    if (profile === null) {
      return;
    }
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [profile, onClose]);

  if (profile === null) {
    return null;
  }
  const titleId = 'profile-view-title';
  const competences = profile.competencesByLevel ?? {};
  const competenceLevels = SENIORITY_LEVELS.filter(
    (level) => (competences[level] ?? []).length > 0,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/40" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold">
              {profile.libelle}
            </h2>
            <p className="font-mono text-xs text-muted-foreground">
              {profile.profileCode} · {profile.famille} / {profile.sousFamille}
            </p>
          </div>
          <Button
            ref={closeRef}
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            Fermer
          </Button>
        </div>

        <dl className="mt-4 space-y-4 text-sm">
          <div>
            <dt className="font-medium text-muted-foreground">Description</dt>
            <dd>{profile.description.length > 0 ? profile.description : 'Non renseigné'}</dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">Niveaux disponibles</dt>
            <dd className="flex flex-wrap gap-1">
              {profile.availableLevels.length > 0
                ? profile.availableLevels.map((lvl) => (
                    <Badge key={lvl} variant="outline">
                      {lvl}
                    </Badge>
                  ))
                : 'Non renseigné'}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">
              Compétences de référence par niveau
            </dt>
            <dd>
              {competenceLevels.length > 0 ? (
                <ul className="space-y-1">
                  {competenceLevels.map((level) => (
                    <li key={level}>
                      <span className="font-medium">{level}</span> :{' '}
                      {(competences[level] ?? []).join(', ')}
                    </li>
                  ))}
                </ul>
              ) : (
                'Non renseigné'
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">Missions-type</dt>
            <dd>
              {profile.missions !== null && profile.missions.length > 0 ? (
                <iframe
                  title="Aperçu des missions-type du profil"
                  sandbox=""
                  srcDoc={profile.missions}
                  className="h-40 w-full rounded-md border border-border bg-background"
                />
              ) : (
                'Non renseigné'
              )}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

/**
 * Éditeur de chips (add/remove de chaînes) pour les compétences d'UN niveau (F0-b). WCAG : chaque chip
 * est un <button> retirable au nom accessible explicite ; l'ajout se fait au clic ou à la touche Entrée
 * (sans soumettre le formulaire parent). Aucune dépendance (design system shadcn : Badge/Input/Button).
 */
function ChipListEditor({
  idBase,
  legend,
  values,
  onChange,
}: {
  readonly idBase: string;
  readonly legend: string;
  readonly values: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
}): React.JSX.Element {
  const [draft, setDraft] = React.useState('');
  const inputId = `${idBase}-input`;

  const add = React.useCallback((): void => {
    const value = draft.trim();
    if (value.length === 0 || values.includes(value)) {
      setDraft('');
      return;
    }
    onChange([...values, value]);
    setDraft('');
  }, [draft, values, onChange]);

  const remove = React.useCallback(
    (value: string): void => {
      onChange(values.filter((v) => v !== value));
    },
    [values, onChange],
  );

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <Label htmlFor={inputId} className="text-sm font-medium">
        {legend}
      </Label>
      {values.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label={`Compétences du niveau ${legend}`}>
          {values.map((value) => (
            <li key={value}>
              <Badge variant="secondary" className="gap-1">
                <span>{value}</span>
                <button
                  type="button"
                  onClick={() => remove(value)}
                  className="rounded-sm px-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Retirer la compétence ${value} du niveau ${legend}`}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Aucune compétence pour ce niveau.</p>
      )}
      <div className="flex gap-2">
        <Input
          id={inputId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // N'ajoute PAS via la soumission du formulaire parent : ajout local à la touche Entrée.
              e.preventDefault();
              add();
            }
          }}
          placeholder="Ajouter une compétence"
          className="h-9"
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>
          Ajouter
        </Button>
      </div>
    </div>
  );
}

/** Slice de `competencesByLevel` (DTO) → map complète sur les 4 niveaux (état de saisie du formulaire). */
function toCompetencesState(
  value: CompetencesByLevelDto | null | undefined,
): Record<SeniorityLevelDto, string[]> {
  const base: Record<SeniorityLevelDto, string[]> = {
    Débutant: [],
    Maîtrisant: [],
    Expert: [],
    Référent: [],
  };
  if (value != null) {
    for (const level of SENIORITY_LEVELS) {
      const list = value[level];
      if (Array.isArray(list)) {
        base[level] = [...list];
      }
    }
  }
  return base;
}

/**
 * Création assistée — code de profil SUGGÉRÉ (DBU §2.4 l.952). Bouton « Suggérer un code » qui
 * appelle l'endpoint `suggest-code` (BACK prêt) puis AFFICHE le candidat en lecture seule, avec
 * « Régénérer ». Le code est INDICATIF : l'allocation réelle (unicité perpétuelle, inv.1) reste au
 * serveur lors du PUT (le formulaire crée sans `profileCode`). WCAG : <label> lié à l'affichage
 * read-only ; message d'état role="status" / d'erreur role="alert".
 */
function SuggestedCodeField({
  suggestedCode,
  isPending,
  isError,
  errorMessage,
  onSuggest,
}: {
  readonly suggestedCode: string | null;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | null;
  readonly onSuggest: () => void;
}): React.JSX.Element {
  const inputId = 'profile-suggested-code';
  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-3">
      <Label htmlFor={inputId}>Code suggéré</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={inputId}
          value={suggestedCode ?? ''}
          readOnly
          placeholder="Aucun code suggéré"
          aria-describedby="profile-suggested-code-hint"
          className="h-9 w-48 font-mono"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSuggest}
          disabled={isPending}
        >
          {isPending
            ? 'Suggestion…'
            : suggestedCode !== null
              ? 'Régénérer'
              : 'Suggérer un code'}
        </Button>
      </div>
      <p id="profile-suggested-code-hint" className="text-xs text-muted-foreground">
        Suggestion indicative : le code définitif est alloué et garanti unique par le
        serveur à la création.
      </p>
      <div aria-live="polite" aria-atomic="true">
        {suggestedCode !== null && !isError ? (
          <p role="status" className="text-xs text-success">
            Code suggéré : {suggestedCode}.
          </p>
        ) : null}
        {isError ? (
          <p role="alert" className="text-xs text-destructive">
            Impossible de suggérer un code : {errorMessage ?? 'erreur inconnue.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ProfileForm({
  catalogVersionId,
  editing,
  onCancelEdit,
  onSaved,
}: {
  readonly catalogVersionId: string;
  readonly editing: ProfileView | null;
  readonly onCancelEdit: () => void;
  /** C2 — notifié à l'enregistrement RÉUSSI (le parent ferme alors le volet latéral). */
  readonly onSaved?: () => void;
}): React.JSX.Element {
  const upsert = useUpsertProfile();
  const suggest = useSuggestProfileCode();
  const [libelle, setLibelle] = React.useState(editing?.libelle ?? '');
  const [famille, setFamille] = React.useState(editing?.famille ?? '');
  const [sousFamille, setSousFamille] = React.useState(editing?.sousFamille ?? '');
  const [description, setDescription] = React.useState(editing?.description ?? '');
  const [contextes, setContextes] = React.useState(editing?.contextes ?? '');
  const [environnements, setEnvironnements] = React.useState(
    editing?.environnements ?? '',
  );
  // Missions = descriptif riche (DBU §2.4 l.950/952). HTML source, nettoyé au write côté API.
  const [missions, setMissions] = React.useState(editing?.missions ?? '');
  const [levels, setLevels] = React.useState<readonly SeniorityLevelDto[]>(
    editing?.availableLevels.filter((l): l is SeniorityLevelDto =>
      (SENIORITY_LEVELS as readonly string[]).includes(l),
    ) ?? [],
  );
  // F0-b — compétences par niveau (saisie sur les 4 niveaux ; seuls les niveaux DISPONIBLES sont envoyés).
  const [competences, setCompetences] = React.useState<
    Record<SeniorityLevelDto, string[]>
  >(toCompetencesState(editing?.competencesByLevel));

  const toggleLevel = React.useCallback((level: SeniorityLevelDto): void => {
    setLevels((prev) =>
      prev.includes(level)
        ? prev.filter((l) => l !== level)
        : [...prev, level],
    );
  }, []);

  const setLevelCompetences = React.useCallback(
    (level: SeniorityLevelDto, next: readonly string[]): void => {
      setCompetences((prev) => ({ ...prev, [level]: [...next] }));
    },
    [],
  );

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      // F0-b — n'envoie QUE les niveaux DISPONIBLES portant ≥ 1 compétence (cohérence clés ⊆ availableLevels ;
      // l'API re-valide). `null` si aucune compétence (effacé).
      const competencesByLevel: CompetencesByLevelDto = {};
      for (const level of levels) {
        const list = competences[level].map((c) => c.trim()).filter((c) => c.length > 0);
        if (list.length > 0) {
          competencesByLevel[level] = list;
        }
      }
      upsert.mutate({
        catalogVersionId,
        input: {
          ...(editing !== null ? { profileCode: editing.profileCode } : {}),
          libelle,
          famille,
          sousFamille,
          description,
          availableLevels: levels,
          // Contextes/environnements (DBU §2.4) : texte vide ⇒ `null` (effacé) côté contrat.
          contextes: contextes.trim().length > 0 ? contextes : null,
          environnements:
            environnements.trim().length > 0 ? environnements : null,
          missions: missions.trim().length > 0 ? missions : null,
          competencesByLevel:
            Object.keys(competencesByLevel).length > 0 ? competencesByLevel : null,
        },
      },
      {
        // C2 — enregistrement RÉUSSI ⇒ le parent ferme le volet latéral (Sheet).
        onSuccess: () => {
          onSaved?.();
        },
      });
    },
    [
      upsert,
      catalogVersionId,
      editing,
      libelle,
      famille,
      sousFamille,
      description,
      contextes,
      environnements,
      missions,
      levels,
      competences,
      onSaved,
    ],
  );

  const mode = editing !== null ? 'edit' : 'create';

  return (
    <form onSubmit={onSubmit} className="space-y-4" aria-describedby="profile-form-mode">
      <p id="profile-form-mode" className="text-sm font-medium text-foreground">
        {mode === 'edit'
          ? `Modification du profil ${editing?.profileCode ?? ''}`
          : 'Nouveau profil'}
      </p>

      {mode === 'create' ? (
        <SuggestedCodeField
          suggestedCode={suggest.data?.profileCode ?? null}
          isPending={suggest.isPending}
          isError={suggest.isError}
          errorMessage={suggest.error?.message ?? null}
          onSuggest={() => {
            suggest.reset();
            suggest.mutate(catalogVersionId);
          }}
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="profile-libelle">Libellé</Label>
          <Input
            id="profile-libelle"
            name="libelle"
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-famille">Famille</Label>
          <Input
            id="profile-famille"
            name="famille"
            value={famille}
            onChange={(e) => setFamille(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-sous-famille">Sous-famille</Label>
          <Input
            id="profile-sous-famille"
            name="sousFamille"
            value={sousFamille}
            onChange={(e) => setSousFamille(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-description">Description</Label>
          <Input
            id="profile-description"
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="profile-contextes">Contextes d’intervention</Label>
          <textarea
            id="profile-contextes"
            name="contextes"
            rows={3}
            value={contextes}
            onChange={(e) => setContextes(e.target.value)}
            className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-environnements">
            Environnements technologiques
          </Label>
          <textarea
            id="profile-environnements"
            name="environnements"
            rows={3}
            value={environnements}
            onChange={(e) => setEnvironnements(e.target.value)}
            className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>

      <div className="space-y-2">
        <RichHtmlEditor
          id="profile-missions"
          value={missions}
          onChange={setMissions}
          label="Missions-type (descriptif riche)"
          hint="Utilisez la barre d'outils pour mettre en forme (titres, listes, gras, italique, souligné, liens, tables). Le contenu est nettoyé et sécurisé à l'enregistrement."
        />
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-foreground">
          Niveaux de séniorité disponibles
        </legend>
        <div className="flex flex-wrap gap-4">
          {SENIORITY_LEVELS.map((level) => {
            const id = `profile-level-${level}`;
            return (
              <span key={level} className="flex items-center gap-2">
                <input
                  id={id}
                  type="checkbox"
                  className="h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  checked={levels.includes(level)}
                  onChange={() => toggleLevel(level)}
                />
                <Label htmlFor={id}>{level}</Label>
              </span>
            );
          })}
        </div>
      </fieldset>

      {levels.length > 0 ? (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-foreground">
            Compétences de référence par niveau
          </legend>
          <p className="text-xs text-muted-foreground">
            Ajoutez les compétences attendues pour chaque niveau disponible du profil.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {SENIORITY_LEVELS.filter((level) => levels.includes(level)).map(
              (level) => (
                <ChipListEditor
                  key={level}
                  idBase={`competences-${level}`}
                  legend={level}
                  values={competences[level]}
                  onChange={(next) => setLevelCompetences(level, next)}
                />
              ),
            )}
          </div>
        </fieldset>
      ) : null}

      <div aria-live="polite" aria-atomic="true">
        {upsert.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {editErrorMessage(upsert.error)}
          </p>
        ) : null}
        {upsert.isSuccess ? (
          <p role="status" className="text-sm text-success">
            Profil enregistré.
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={upsert.isPending}>
          {upsert.isPending
            ? 'Enregistrement…'
            : mode === 'edit'
              ? 'Enregistrer les modifications'
              : 'Créer le profil'}
        </Button>
        {mode === 'edit' ? (
          <Button type="button" variant="ghost" onClick={onCancelEdit}>
            Nouveau profil
          </Button>
        ) : null}
      </div>
    </form>
  );
}
